import { EditorView } from '@codemirror/view';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags as t } from '@lezer/highlight';
import type { Extension } from '@codemirror/state';

/**
 * A calm, token-driven CodeMirror theme. Colors resolve from the app's OKLCH
 * CSS custom properties so the editor tracks the light/dark theme with no flash.
 */
const baseTheme = EditorView.theme({
  '&': {
    color: 'var(--mb-fg)',
    backgroundColor: 'var(--mb-editor-bg)',
    height: '100%',
    fontSize: 'var(--mb-editor-font-size, 15px)',
  },
  '.cm-content': {
    fontFamily: 'var(--mb-font-mono, ui-monospace, "JetBrains Mono", monospace)',
    caretColor: 'var(--mb-accent)',
    padding: '1rem 0',
    lineHeight: '1.7',
  },
  '.cm-scroller': {
    fontFamily: 'var(--mb-font-mono, ui-monospace, "JetBrains Mono", monospace)',
    overflow: 'auto',
  },
  '&.cm-focused .cm-cursor': { borderLeftColor: 'var(--mb-accent)' },
  '.cm-gutters': {
    backgroundColor: 'transparent',
    color: 'var(--mb-fg-muted)',
    border: 'none',
  },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, ::selection': {
    backgroundColor: 'var(--mb-selection)',
  },
  '.cm-activeLine': { backgroundColor: 'transparent' },
  '.cm-line': { padding: '0 1.25rem' },

  // Find & replace panel — brand-styled.
  '.cm-panels': {
    backgroundColor: 'var(--mb-panel-bg)',
    color: 'var(--mb-fg)',
    borderBottom: '1px solid var(--mb-border)',
  },
  '.cm-panel.cm-search': {
    padding: '0.5rem 0.75rem',
    fontFamily: 'var(--mb-font-body)',
    fontSize: '0.85rem',
  },
  '.cm-panel.cm-search input, .cm-panel.cm-search button, .cm-panel.cm-search label': {
    fontFamily: 'inherit',
    fontSize: '0.85rem',
  },
  '.cm-panel.cm-search input': {
    background: 'var(--mb-editor-bg)',
    color: 'var(--mb-fg)',
    border: '1px solid var(--mb-border)',
    borderRadius: '4px',
    padding: '0.2rem 0.4rem',
  },
  '.cm-panel.cm-search button': {
    background: 'transparent',
    color: 'var(--mb-fg)',
    border: '1px solid var(--mb-border)',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  '.cm-panel.cm-search .cm-button': { backgroundImage: 'none' },
  '.cm-searchMatch': { backgroundColor: 'var(--mb-selection)' },
  '.cm-searchMatch-selected': { backgroundColor: 'var(--mb-accent)', color: 'var(--mb-accent-fg)' },

  // Slash / autocomplete menu.
  '.cm-tooltip.cm-tooltip-autocomplete': {
    background: 'var(--mb-panel-bg)',
    border: '1px solid var(--mb-border)',
    borderRadius: '8px',
    boxShadow: 'var(--mb-shadow)',
    overflow: 'hidden',
  },
  '.cm-tooltip-autocomplete ul li': {
    fontFamily: 'var(--mb-font-body)',
    padding: '0.3rem 0.6rem',
    color: 'var(--mb-fg)',
  },
  '.cm-tooltip-autocomplete ul li[aria-selected]': {
    background: 'var(--mb-selection)',
    color: 'var(--mb-fg)',
  },
  '.cm-completionLabel': { fontFamily: 'var(--mb-font-mono)' },
  '.cm-completionDetail': {
    color: 'var(--mb-fg-muted)',
    fontStyle: 'normal',
    marginLeft: '0.5rem',
  },
});

// The accent (kingfisher teal) is a *precise flash*, not a wash: it marks
// markdown punctuation and links, while prose and heading text stay neutral so
// syntax hierarchy reads clearly.
const markdownHighlight = HighlightStyle.define([
  // Heading text: strong but neutral (not teal). Its `#` mark gets the accent
  // via processingInstruction below.
  { tag: t.heading, color: 'var(--mb-fg)', fontWeight: '650' },
  { tag: t.strong, fontWeight: '700', color: 'var(--mb-fg)' },
  { tag: t.emphasis, fontStyle: 'italic' },
  { tag: t.strikethrough, textDecoration: 'line-through' },
  { tag: t.link, color: 'var(--mb-accent)', textDecoration: 'underline' },
  { tag: t.url, color: 'var(--mb-fg-muted)' },
  { tag: [t.monospace], color: 'var(--mb-syntax-code)' },
  { tag: t.quote, color: 'var(--mb-fg-muted)', fontStyle: 'italic' },
  // Markdown marks (#, *, _, `, -, >, link brackets) — the teal flash.
  { tag: t.processingInstruction, color: 'var(--mb-accent)' },
  { tag: [t.keyword], color: 'var(--mb-syntax-keyword)' },
  { tag: [t.string], color: 'var(--mb-syntax-string)' },
  { tag: [t.comment], color: 'var(--mb-fg-muted)', fontStyle: 'italic' },
  { tag: [t.number], color: 'var(--mb-syntax-number)' },
  { tag: [t.meta], color: 'var(--mb-fg-muted)' },
]);

export const markbirdEditorTheme: Extension = [baseTheme, syntaxHighlighting(markdownHighlight)];
